/**
 * Courts that have not answered a recorded discovery request inside five business days.
 *
 * An empty list is not "every court answered" when no request dates exist on file. The copy below
 * has to say which.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DiscoveryListsApi, {
  OverdueAcknowledgmentRow,
  OverdueAcknowledgmentsDto,
} from '../../api/DiscoveryListsApi';

function dateLabel(value: string): string {
  return value.length >= 10 ? value.slice(0, 10) : value;
}

const ManagerOverdueAcknowledgmentsView: React.FC = () => {
  const navigate = useNavigate();
  const [dto, setDto] = useState<OverdueAcknowledgmentsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDto(await DiscoveryListsApi.overdueAcknowledgments());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load overdue acknowledgments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows: OverdueAcknowledgmentRow[] = dto?.overdue ?? [];
  const datesNeverRecorded = dto != null && dto.casesWithRequestDate === 0;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h4" gutterBottom>
        Overdue acknowledgments
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Cases where a discovery request date is on file and the court has not answered inside five
        business days.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : datesNeverRecorded ? (
        <Alert severity="info">
          No discovery request dates have been recorded ({dto.casesWithRequestDate} of the cases on
          file). This list is empty because the dates were never written down, not because every
          court has answered.
        </Alert>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            As of {dto?.asOf}. {dto?.awaitingAnswer} request(s) still waiting for an answer.
            {dto?.casesWithRequestDate} case(s) have a request date on file.
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell>Citation</TableCell>
                  <TableCell>Court</TableCell>
                  <TableCell>Requested</TableCell>
                  <TableCell>Deadline</TableCell>
                  <TableCell>Business days overdue</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        No courts are past the five-business-day window right now.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow
                      key={row.ticketGuid}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/ticket?Ticket_guid=${row.ticketGuid}`)}
                    >
                      <TableCell>{row.clientName}</TableCell>
                      <TableCell>{row.citationNumber || '—'}</TableCell>
                      <TableCell>{row.courtName || '—'}</TableCell>
                      <TableCell>{dateLabel(row.requestedAt)}</TableCell>
                      <TableCell>{dateLabel(row.deadline)}</TableCell>
                      <TableCell>{row.businessDaysOverdue}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Container>
  );
};

export default ManagerOverdueAcknowledgmentsView;
